import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField } from '@/components/forms';
import { appTzLabel, fromDateRange, isAfterAppToday, toDayjsRange } from '@/lib/date';

import { toDefaults, toFilters } from '../filters';
import { INSTALLATION_STATUS_CHIP } from '../format';
import { useInstallationAssignees } from '../hooks';
import { installationFiltersSchema } from '../schemas';
import type { AssigneeType, InstallationFilterFormValues, InstallationListFilters } from '../types';

import styles from './installation-filter-drawer.module.scss';

const STATUS_OPTIONS = (
  Object.keys(INSTALLATION_STATUS_CHIP) as (keyof typeof INSTALLATION_STATUS_CHIP)[]
).map((value) => ({ value, label: INSTALLATION_STATUS_CHIP[value].label }));

const ASSIGNEE_TYPE_OPTIONS: { value: AssigneeType; label: string }[] = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

export type InstallationFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: InstallationListFilters;
  onApply: (filters: InstallationListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<InstallationFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<InstallationFilterFormValues>({
    resolver: zodResolver(installationFiltersSchema),
    defaultValues: {
      ...draft,
      orderDateFrom: draft.orderDateFrom ?? null,
      orderDateTo: draft.orderDateTo ?? null,
    },
    mode: 'onBlur',
  });

  const assignees = useInstallationAssignees();
  const assigneeOptions = useMemo(
    () =>
      (assignees.data ?? []).map((a) => ({ value: a.id, label: `${a.firstName} ${a.lastName}` })),
    [assignees.data],
  );

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  // Single range control bound to the two draft fields (keeps the wire contract + the
  // From<=To validation on orderDateFrom/orderDateTo; Figma 1927:219388 draws one field).
  const [orderedFrom, orderedTo] = useWatch({
    control: form.control,
    name: ['orderDateFrom', 'orderDateTo'],
  });
  const orderedError = form.formState.errors.orderDateTo?.message;

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={464}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>No of Devices</legend>
        <div className={styles.row}>
          <NumberField
            name="deviceCountFrom"
            control={form.control}
            aria-label="No of Devices From"
            placeholder="From"
            min={1}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="deviceCountTo"
            control={form.control}
            aria-label="No of Devices To"
            placeholder="To"
            min={1}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <MultiSelectField
        name="assigneeIds"
        control={form.control}
        label="Assignee"
        placeholder="Select"
        loading={assignees.isLoading}
        options={assigneeOptions}
      />

      <MultiSelectField
        name="assigneeTypes"
        control={form.control}
        label="Assignee Type"
        placeholder="Select"
        options={ASSIGNEE_TYPE_OPTIONS}
      />

      <MultiSelectField
        name="statuses"
        control={form.control}
        label="Installation Status"
        placeholder="Select"
        options={STATUS_OPTIONS}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Order Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Order Date Range')}
          format="MM/DD/YYYY"
          disabledDate={isAfterAppToday}
          style={{ width: '100%' }}
          status={orderedError ? 'error' : undefined}
          value={toDayjsRange(orderedFrom && orderedTo ? [orderedFrom, orderedTo] : null)}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('orderDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('orderDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
        {orderedError ? <p className={styles.error}>{orderedError}</p> : null}
      </fieldset>
    </Drawer>
  );
}

export function InstallationFilterDrawer({ open, ...rest }: InstallationFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
